/**
 * SectionDataHeatmap is responsible for rendering a full interactive heatmap for an entire section
 * of data e.g. THz, Inrared, metal, etc.
 */

import React, { useEffect, useRef  } from "react"
import colormap from 'colormap'
import { Position, SectionDataHeatmapProps } from "./SectionDataHeatmap.types"
import "./SectionDataHeatmap.css"

const SectionDataHeatmapVertical = (props: SectionDataHeatmapProps) => {

  const {
    heatmaps,
    heatmapMask,
    width: VIEW_WIDTH,
    scanPixelHeight: pixelHeight,
    selectedScan,
    scanPositionMetadata,
    overlayBoxes,
    highlightMin,
    highlightMax,
    interactionEnabled,
    colourPalette: COLOUR_PALETTE,
    canvasRef,
    skippedIndexes,
    onSelectScan
  } = props

  /**
   * Mouse position on the canvas.
   */
  const mousePositionRef = useRef<Position|null>(null)

  /**
   * Selected scan.
   */
  const selectedScanRef = useRef<number>(0)

  /**
   * Number of pixels gap between scans.
   */
  const SCAN_SPACING = 2

  /**
   * Number of pixels for the height of the canvas.
   */
  const CANVAS_HEIGHT = VIEW_WIDTH / 2

  /**
   * Size of each heatmap pixel.
   */
  const PIXEL_SIZE = { width: pixelHeight, height: (CANVAS_HEIGHT/heatmaps[0][0].length)} // TODO: Rethink this for large number of scans

  /**
   * Number of pixels for the width of the canvas.
   */
  const CANAVAS_WIDTH = (heatmaps.length * PIXEL_SIZE.width * heatmaps[0].length) + (heatmaps.length* SCAN_SPACING)

  /**
   * Number of rows per scan.
   */
  const SCAN_ROW_WIDTH = Math.floor(CANAVAS_WIDTH / heatmaps.length)

  /** 
   * Number of pixels width for an entire scan column.
   */
  const SCAN_PIXEL_WIDTH = heatmaps[0].length * PIXEL_SIZE.width

  /**
   * Threshold around 0 and 255 to trigger highlighting of min and max values.
   */
  const DATA_HIGHLIGHT_THRESHOLD = 5

  /**
   * heatmap colour map palette.
   */
  const colourMap = colormap({
    colormap: COLOUR_PALETTE,
    nshades: 256,
    format: 'hex',
    alpha: 1
  })

  useEffect(() => {
    selectedScanRef.current = selectedScan    
    draw()
  }, [canvasRef, PIXEL_SIZE, VIEW_WIDTH, CANAVAS_WIDTH, selectedScan, highlightMax, highlightMin, overlayBoxes, heatmaps, heatmapMask])

  /**
   * Draws all data onto the canvas i.e. heatmaps and highlighted scans.
   * 
   * @param context canvas context
   */
  const draw = () => {
    if (canvasRef?.current === null) { return }
    const context = canvasRef.current.getContext('2d', { willReadFrequently: true })
    if (context === null) { return }

    context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
    drawHeatmap(context)
    if (interactionEnabled === false) { return }
    drawScanMouseHoverHighlight(context)
    drawSelectedScanHighlight(context)
    drawScanInfoBox(context)
    // drawBoundingBoxes(context)
  }

  /**
   * Draws full sections data onto the canvas as a heatmap.
   * 
   * @param context canvas context
   */
  const drawHeatmap = (context: CanvasRenderingContext2D) => {
    for (let scan=0; scan < heatmaps.length; scan++) {
      const scanColours = heatmaps[heatmaps.length-1-scan]
      const scanLength = scanColours.length-1
      
      for (let channel=0; channel < scanColours.length; channel++) {
        const scanRow = scanColours[scanLength-channel]
        const rowLength = scanRow.length

        for (let i=0; i<rowLength; i++) {
          context.fillStyle = sampleToHexColour(scanRow[i])

          if (heatmapMask && heatmapMask[scan] && heatmapMask[scan][i] === 0) {
            context.fillStyle = "transparent"
          }

          if (skippedIndexes !== undefined && skippedIndexes.includes(scan)) {
            context.fillStyle = "#2F2E35"
          }

          context.fillRect(
            Math.floor(channel * PIXEL_SIZE.width) + (scanColours.length*scan*PIXEL_SIZE.width)  + (SCAN_SPACING*scan), 
            Math.floor(i * PIXEL_SIZE.height),
            Math.ceil(PIXEL_SIZE.width), 
            Math.ceil(PIXEL_SIZE.height)
          )
        }
      }
    }
  }

  /**
   * Draws highlighted box around scans based on mouse is hovering position.
   * 
   * @param context canvas context
   */
  const drawScanMouseHoverHighlight = (context: CanvasRenderingContext2D) => {
    if (mousePositionRef.current === null) { return }
    const scanNumber = Math.floor(mousePositionRef.current.x / SCAN_ROW_WIDTH)
    drawScanHighlight(scanNumber, context)
  }

  /**
   * Draws highlighted around the selected scan.
   * 
   * @param context canvas context
   */
  const drawSelectedScanHighlight = (context: CanvasRenderingContext2D) => {    
    drawScanHighlight(selectedScanRef.current, context)
  }

  /**
   * Draws a highlighted border round a given scan.
   * 
   * @param scanNumber to draw box round
   * @param context canvas context
   */
  const drawScanHighlight = (scanNumber: number, context: CanvasRenderingContext2D) => {
    const x = scanNumber*SCAN_ROW_WIDTH

    context.strokeStyle = "white"
    context.lineWidth = 2
    context.beginPath()
    context.roundRect(x, 0, SCAN_ROW_WIDTH, CANVAS_HEIGHT, 5)
    context.stroke()
  }

  /**
   * Draws scan info box overlay for a scan.
   * 
   * @param context canvas context
   */
  const drawScanInfoBox = (context: CanvasRenderingContext2D) => {
    if (mousePositionRef.current === null) { return }
    const scanNumber = Math.floor(mousePositionRef.current.x / SCAN_ROW_WIDTH)
    const text = scanPositionMetadata[scanNumber]

    const boxThickness = 22 // short dimension (was boxHeight)
    const boxLength = 120    // long dimension (was boxWidth)
    const offsetTop = 20 * PIXEL_SIZE.height
    const offsetLeft = 1 * PIXEL_SIZE.width
    const textPadding = 2 * PIXEL_SIZE.width

    // Fall back to vertical layout when there isn't enough width to
    // fit the box horizontally. Tweak this threshold to taste.
    const isVertical = CANAVAS_WIDTH < boxLength * 2

    context.fillStyle = "#19161F"
    context.font = `400 13px Hanken Grotesk`
    context.textAlign = "left"
    context.textBaseline = "middle"

    let x = scanNumber * SCAN_ROW_WIDTH

    if (isVertical) {
      // box: boxThickness wide, boxLength tall
      if (x + boxThickness > CANAVAS_WIDTH) {
        x = (x + SCAN_PIXEL_WIDTH) - boxThickness - offsetLeft
      } else {
        x = x + offsetLeft
      }

      context.beginPath()
      context.roundRect(x, offsetTop, boxThickness, boxLength, 5)
      context.fill()

      context.fillStyle = "white"
      context.save()
      // Rotate around a point near the bottom of the box so text
      // reads bottom-to-top inside it.
      context.translate(x + boxThickness / 2, offsetTop + boxLength - textPadding)
      context.rotate(-Math.PI / 2)
      context.fillText(text, 0, 0, boxLength - textPadding * 2)
      context.restore()
    } else {
      // original horizontal layout
      if (x + boxLength > CANAVAS_WIDTH) {
        x = (x + SCAN_PIXEL_WIDTH) - boxLength - offsetLeft
      } else {
        x = x + offsetLeft
      }

      context.beginPath()
      context.roundRect(x, offsetTop, boxLength, boxThickness, 5)
      context.fill()

      context.fillStyle = "white"
      context.fillText(
        text,
        x + textPadding,
        offsetTop + (boxThickness / 2),
        boxLength - textPadding * 2
      )
    }
  }

  /**
   * Draws a set of overlay bounding boxes.
   * 
   * @param context canvas context
   */
  const drawBoundingBoxes = (context: CanvasRenderingContext2D) => {
    if (overlayBoxes === undefined) { return }

    for (let i=0; i<overlayBoxes.length; i++) {
      const vector = overlayBoxes[i]
      context.strokeStyle = "#6ADCF2"
      context.lineWidth = 4
      context.beginPath()
      context.roundRect(
        vector[0] * PIXEL_SIZE.width,
        vector[1] * PIXEL_SIZE.height,
        vector[2] * PIXEL_SIZE.width,
        vector[3] * PIXEL_SIZE.height,
        5
      )
      context.stroke()
    }
  }

  /**
   * Selects a scan based on mouse click.
   * 
   * @param _ mouse down event
   */
  const onCanvasMouseDown = (_: React.MouseEvent<HTMLCanvasElement>) => {
    if (mousePositionRef.current === null) { return }
    const scanNumber = Math.floor(mousePositionRef.current.x / SCAN_ROW_WIDTH)
    onSelectScan(scanNumber)
    draw()
  }

  /**
   * Redraws canvas without mouse scan highlight.
   */
  const onMouseLeaveCanvas = () => {
    mousePositionRef.current = null
    draw()
  }

  /**
   * Sets mouse x, y position on the canvas.
   * 
   * @param e mouse event
   */
  const setMousePos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    const x = (e.clientX - rect.left) * scaleX
    const y = (e.clientY - rect.top) * scaleY  

    if (mousePositionRef.current === null) {
      mousePositionRef.current = { x: x, y: y }
    } else {
      mousePositionRef.current.x = x
      mousePositionRef.current.y = y
    }
    draw()
  }

  /**
   * Converts a given 0-255 number to hex colour string.
   * 
   * @param sample 0-255 colour value to get hex value for
   * 
   * @returns hex colour string for a given 0-255 value
   */
  const sampleToHexColour = (sample: number): string => {
    // if (sample === 0) { return "transparent"}
    if (Math.ceil(sample) > 255 - DATA_HIGHLIGHT_THRESHOLD && highlightMax) { return "#ee0c0c"}
    if (Math.ceil(sample) < 0 + DATA_HIGHLIGHT_THRESHOLD && highlightMin) { return "#1282A2"}

    const i = Math.ceil(Math.max(0, Math.min(255, sample)))    
    return colourMap[i]
  }

  return (
    <div>
      <canvas
        className={`${(interactionEnabled === false) ? "" : "data-heatmap"}`}
        style={{borderRadius: "5px"}}
        ref={canvasRef} 
        width={CANAVAS_WIDTH}
        height={CANVAS_HEIGHT}
        onMouseMove={setMousePos}
        onMouseDown={onCanvasMouseDown}
        onPointerLeave={onMouseLeaveCanvas}
      />
    </div>
  )
}

export default SectionDataHeatmapVertical